import { useState, useEffect } from 'react';
import bankOfAmericaIcon from '../assets/images/bank-of-america-icon.svg';
import lowesIcon from '../assets/images/lowes-icon.svg';

function Experience() {
  const logoMap = {
    'bank-of-america-icon.svg': bankOfAmericaIcon,
    'lowes-icon.svg': lowesIcon,
  };
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/data/experiences.json')
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch experiences');
        }
        return res.json();
      })
      .then((data) => setExperiences(data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-white text-xl">Loading experiences...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-red-400 text-xl">
          Error:
          {' '}
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <section id="experience" className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-cyan-400 mb-4">
              Experience
            </h1>
          </div>

          {/* Experience Cards */}
          <div className="space-y-8">
            {experiences.map((exp) => (
              <div
                key={exp.id}
                className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:border-cyan-400 transition-colors"
              >
                {/* Company and Title */}
                <div className="mb-4">
                  <div className="flex items-center justify-center gap-4 mb-2">
                    {exp.logo && logoMap[exp.logo] && (
                      <img
                        src={logoMap[exp.logo]}
                        alt={`${exp.company} logo`}
                        className="h-12 w-12 object-contain"
                      />
                    )}
                    <h2 className="text-purple-400 text-2xl font-bold">
                      {exp.company}
                    </h2>
                  </div>
                  <h3 className="text-cyan-400 text-xl font-semibold mb-2">
                    {exp.jobTitle}
                  </h3>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-gray-400 text-sm">
                    <span>
                      {formatDate(exp.startDate)}
                      {' '}
                      -
                      {' '}
                      {formatDate(exp.endDate)}
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span>{exp.location}</span>
                  </div>
                </div>

                {/* Description */}
                {exp.description && (
                  <p className="text-gray-300 mb-4">{exp.description}</p>
                )}

                {/* Responsibilities */}
                {exp.responsibilities && exp.responsibilities.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-white font-semibold mb-2">
                      Key Responsibilities:
                    </h4>
                    <ul className="space-y-2 list-none p-0 m-0">
                      {exp.responsibilities.map((responsibility) => (
                        <li
                          key={responsibility}
                          className="text-gray-300 p-0 m-0 text-left before:content-['•'] before:text-cyan-400 before:mr-2 before:inline-block"
                        >
                          {responsibility}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Technologies */}
                {exp.technologies && exp.technologies.length > 0 && (
                  <div>
                    <h4 className="text-white font-semibold mb-2">
                      Technologies:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {exp.technologies.map((tech) => (
                        <span
                          key={tech}
                          className="bg-gray-700 text-cyan-400 px-3 py-1 rounded-full text-sm border border-cyan-400/30"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Experience;
